<template>
  <BottomSheet ref="bottomSheet" hashBase="details">
    <!-- Body -->
    <div class="px-6 py-6 pb-12 overflow-y-auto max-h-[80vh]" v-if="purchase">
      <h3 class="text-2xl font-bold title-color mb-6">Détails de l'achat</h3>
        <div class="space-y-4">
          <!-- Product Name & Status -->
          <div class="flex flex-col gap-2">
            <div class="text-lg font-bold text-gray-900">{{ purchase.productName }}</div>
            <div class="flex justify-between items-center">
              <div class="text-xs text-gray-500 font-mono">ID: {{ purchase.id }}</div>
              <div
                :class="getStatusClass(purchase.status)"
                class="px-3 py-1 rounded-full text-xs font-bold text-white shadow-sm"
              >
                {{ getStatusLabel(purchase.status) }}
              </div>
            </div>
          </div>

          <div class="h-px bg-gray-100 w-full my-4"></div>

          <!-- Order details -->
          <div class="grid grid-cols-2 gap-x-4 gap-y-5 bg-gray-50 p-4 rounded-xl">
            <div>
              <div class="text-[11px] uppercase tracking-wider font-bold text-gray-500 mb-1">Quantité</div>
              <div class="font-bold text-gray-900">{{ purchase.quantity }}</div>
            </div>
            <div>
              <div class="text-[11px] uppercase tracking-wider font-bold text-gray-500 mb-1">Prix unitaire</div>
              <div class="font-bold text-gray-900">{{ formatPrice(purchase.unitPrice) }}</div>
            </div>
            <div class="col-span-2">
              <div class="text-[11px] uppercase tracking-wider font-bold text-gray-500 mb-1">Date d'achat</div>
              <div class="font-medium text-gray-900">{{ formatDate(purchase.createdAt) }}</div>
            </div>
            <div class="col-span-2 pt-2 border-t border-gray-200 mt-1 flex justify-between items-center">
              <div class="text-xs uppercase tracking-wider font-bold text-gray-500">Total payé</div>
              <div class="text-xl font-black text-primary">{{ formatPrice(purchase.totalPrice) }}</div>
            </div>
          </div>

          <!-- Delivery details -->
          <div class="space-y-3 mt-4">
            <h4 class="font-bold text-gray-900 text-sm">Informations de livraison</h4>
            <div class="bg-gray-50 rounded-xl p-4 space-y-3 text-sm">
              <div class="flex flex-col gap-1">
                <span class="text-xs font-bold uppercase tracking-wider text-gray-500">Adresse</span>
                <span class="font-medium text-gray-900 break-words">{{ purchase.deliveryAddress || 'Non spécifiée' }}</span>
              </div>
              <div class="flex flex-col gap-1 pt-2 border-t border-gray-200">
                <span class="text-xs font-bold uppercase tracking-wider text-gray-500">Téléphone</span>
                <span class="font-medium text-gray-900 break-words">{{ purchase.phone || 'Non spécifié' }}</span>
              </div>
            </div>
          </div>

          <!-- Payment Details -->
          <div v-if="purchase.paymentMethod" class="space-y-3 mt-4">
            <h4 class="font-bold text-gray-900 text-sm">Informations de paiement</h4>
            <div class="bg-gray-50 rounded-xl p-4 space-y-3 text-sm">
              <div class="flex justify-between items-center">
                <span class="text-gray-500 font-medium">Méthode</span>
                <span class="font-bold text-gray-900 capitalize">{{ formatPaymentMethod(purchase.paymentMethod) }}</span>
              </div>
              <div v-if="purchase.paymentProvider" class="flex justify-between items-center pt-2 border-t border-gray-200">
                <span class="text-gray-500 font-medium">Réseau</span>
                <span class="font-bold text-gray-900 capitalize">{{ purchase.paymentProvider }}</span>
              </div>
              <div v-if="purchase.paymentId" class="flex justify-between items-center pt-2 border-t border-gray-200">
                <span class="text-gray-500 font-medium">Réf. transaction</span>
                <span class="font-bold text-gray-900 text-xs font-mono break-all w-2/3 text-right">{{ purchase.paymentId }}</span>
              </div>
            </div>
          </div>

        </div>
      </div>
  </BottomSheet>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import Events from '@/events/events'
import BottomSheet from '@/presentation/components/dialog/BottomSheet.vue'

const bottomSheet = ref(null)
const purchase = ref(null)

let handlerId = null

onMounted(() => {
  console.log('✅ ProductPurchaseDetailsModal mounted')
  handlerId = Events.handle('open_purchase_details_modal', (payload) => {
    console.log('📩 open_purchase_details_modal received', payload)
    open(payload)
  })
})

onUnmounted(() => {
  if (handlerId) {
    Events.removeHandler(handlerId)
  }
})

function open(payload) {
  if (payload) {
    purchase.value = payload
    bottomSheet.value?.show()
  }
}

function close() {
  bottomSheet.value?.hide()
}

function getStatusLabel(status) {
  const labels = {
    non_paye: 'Non payé',
    paye: 'Payé',
    en_cours_de_livraison: 'En livraison',
    delivre: 'Livré',
  }
  return labels[status] || status
}

function getStatusClass(status) {
  const classes = {
    non_paye: 'bg-yellow-500',
    paye: 'bg-green-500',
    en_cours_de_livraison: 'bg-blue-500',
    delivre: 'bg-primary-500',
  }
  return classes[status] || 'bg-gray-500'
}

function formatDate(timestamp) {
  if (!timestamp) return '-'
  const date = new Date(timestamp)
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatPrice(price) {
  if (!price) return '0 FCFA'
  return new Intl.NumberFormat('fr-BJ', {
    style: 'currency',
    currency: 'XOF',
    maximumFractionDigits: 0
  }).format(price)
}

function formatPaymentMethod(method) {
  if (method === 'wallet') return 'Portefeuille'
  if (method === 'mobile_money') return 'Mobile Money'
  return method
}


</script>

<script>
export default {
  name: 'ProductPurchaseDetailsModal'
}
</script>
